/**
 * Llamadas a la API de tareas y de personas, más el armado de los filtros de la lista.
 */
import {
  ESTADOS_CERRADOS,
  leerNumeroTarea,
  listaTareasSchema,
  resultadoTareaSchema,
  tareaDetalleSchema,
  usuarioSchema,
  type ActualizarTareaEntrada,
  type AgregarBloqueoEntrada,
  type CrearTareaEntrada,
  type DescarteEntrada,
  type Estado,
  type Prioridad,
  type Tipo,
} from '@cdtec-backlog/contrato';
import { z } from 'zod';
import { solicitar } from '../../lib/api';

/**
 * Filtro «Estado» del panel: qué tareas mostrar según si están abiertas o cerradas.
 * `abiertas` (por defecto) = todo menos lo cerrado; `cerradas` = hechas y descartadas;
 * `hechas` / `descartadas` = solo ese cierre; `todas` = abiertas y cerradas.
 */
export type FiltroEstado = 'abiertas' | 'cerradas' | 'hechas' | 'descartadas' | 'todas';

/** Opciones del filtro «Estado», en el orden del select. */
export const OPCIONES_ESTADO: { valor: FiltroEstado; etiqueta: string }[] = [
  { valor: 'abiertas', etiqueta: 'Abiertas' },
  { valor: 'cerradas', etiqueta: 'Cerradas' },
  { valor: 'hechas', etiqueta: 'Hechas' },
  { valor: 'descartadas', etiqueta: 'Descartadas' },
  { valor: 'todas', etiqueta: 'Todas' },
];

/** Qué estados pide cada filtro de solo cerradas (`?estado=` de la API). */
const ESTADOS_DEL_FILTRO: Partial<Record<FiltroEstado, readonly Estado[]>> = {
  cerradas: ESTADOS_CERRADOS,
  hechas: ['HECHO'],
  descartadas: ['DESCARTADA'],
};

/** Filtros tal como los maneja la pantalla. */
export interface FiltrosUI {
  texto: string;
  prioridad: Prioridad | '';
  tipo: Tipo | '';
  /** Vacío = todos; `sin-asignar`; o el id de una persona en texto. */
  responsable: string;
  /** Vacío = todos; `sin-proyecto`; o el id de un proyecto en texto. */
  proyecto: string;
  soloRiego: boolean;
  /** Solo las tareas bloqueadas (con algún bloqueante abierto): `?bloqueada=true`. */
  soloBloqueadas: boolean;
  /** Solo las tareas atrasadas (fecha límite pasada y abiertas; CDT-76): `?atrasada=true`. */
  soloAtrasadas: boolean;
  estado: FiltroEstado;
}

export const FILTROS_INICIALES: FiltrosUI = {
  texto: '',
  prioridad: '',
  tipo: '',
  responsable: '',
  proyecto: '',
  soloRiego: false,
  soloBloqueadas: false,
  soloAtrasadas: false,
  estado: 'abiertas',
};

/** Todo, incluidas las cerradas: sirve para los totales del resumen. */
export const FILTROS_TODAS: FiltrosUI = { ...FILTROS_INICIALES, estado: 'todas' };

/** Traduce los filtros de la pantalla a la cadena de consulta de la API. */
export function aConsulta(filtros: FiltrosUI): string {
  const parametros = new URLSearchParams({ limite: '1000' });
  if (filtros.texto.trim()) parametros.set('q', filtros.texto.trim());
  if (filtros.prioridad) parametros.set('prioridad', filtros.prioridad);
  if (filtros.tipo) parametros.set('tipo', filtros.tipo);
  if (filtros.responsable) parametros.set('responsable', filtros.responsable);
  if (filtros.proyecto) parametros.set('proyecto', filtros.proyecto);
  if (filtros.soloRiego) parametros.set('tocaRiego', 'true');
  if (filtros.soloBloqueadas) parametros.set('bloqueada', 'true');
  // «Hoy» lo decide la API con el día de Chile (igual que `estaAtrasada` en la tarjeta).
  if (filtros.soloAtrasadas) parametros.set('atrasada', 'true');
  const estados = ESTADOS_DEL_FILTRO[filtros.estado];
  if (estados) parametros.set('estado', estados.join(','));
  if (filtros.estado === 'todas') parametros.set('incluirCerradas', 'true');
  return parametros.toString();
}

/** Enlace del CSV: lleva los mismos filtros que la pantalla (incluido el proyecto). */
export const urlExportarCsv = (filtros: FiltrosUI) =>
  `/api/tareas/exportar.csv?${aConsulta(filtros)}`;

/** Máximo de «Tareas parecidas» que se muestran al crear una tarea. */
export const MAX_PARECIDAS = 5;

/**
 * Consulta de «Tareas parecidas»: basta con que coincida una palabra (`palabras=alguna`), abiertas y
 * cerradas. Trae varias para luego ordenarlas en la web por cuántas palabras comparten con el título.
 */
export function consultaParecidas(titulo: string): string {
  return new URLSearchParams({
    q: titulo.trim(),
    palabras: 'alguna',
    incluirCerradas: 'true',
    limite: '50',
  }).toString();
}

/**
 * Subtareas de una madre (ADR-0018), abiertas y cerradas. Trae las tareas completas (con su creador), así la
 * ficha sabe de cuáles puede quitar la madre quien mira, y el diálogo de cierre lista las abiertas.
 */
export function consultaSubtareas(madreId: number): string {
  return new URLSearchParams({
    padre: String(madreId),
    incluirCerradas: 'true',
    limite: '1000',
  }).toString();
}

/** Máximo de resultados del buscador de tareas (`BuscadorTarea`). */
export const MAX_RESULTADOS_BUSCADOR = 20;

/**
 * Consulta del buscador de tareas: «CDT-12», «cdt 12» o «12» buscan ese número (la API lo entiende como
 * `12`); cualquier otra cosa busca por palabras. Abiertas y cerradas.
 */
export function consultaBuscador(texto: string): string {
  const numero = leerNumeroTarea(texto);
  return new URLSearchParams({
    q: numero === null ? texto.trim() : String(numero),
    incluirCerradas: 'true',
    limite: String(MAX_RESULTADOS_BUSCADOR),
  }).toString();
}

export const listarTareas = (consulta: string) =>
  solicitar(`/tareas?${consulta}`, { esquema: listaTareasSchema });

export const obtenerTarea = (id: number) =>
  solicitar(`/tareas/${id}`, { esquema: tareaDetalleSchema });

export const crearTarea = (entrada: Partial<CrearTareaEntrada> & { titulo: string }) =>
  solicitar('/tareas', { metodo: 'POST', cuerpo: entrada, esquema: resultadoTareaSchema });

export const actualizarTarea = (id: number, cambios: ActualizarTareaEntrada) =>
  solicitar(`/tareas/${id}`, { metodo: 'PATCH', cuerpo: cambios, esquema: resultadoTareaSchema });

/** Mueve la tarea; para descartarla, `descarte` lleva el motivo (obligatorio en la API). */
export const moverTarea = (id: number, estado: Estado, descarte?: DescarteEntrada) =>
  solicitar(`/tareas/${id}/mover`, {
    metodo: 'POST',
    cuerpo: descarte ? { estado, descarte } : { estado },
    esquema: resultadoTareaSchema,
  });

export const agregarNota = (id: number, texto: string) =>
  solicitar(`/tareas/${id}/notas`, {
    metodo: 'POST',
    cuerpo: { texto },
    esquema: tareaDetalleSchema,
  });

/** «`id` bloqueada por `bloqueanteId`» (ADR-0018). Responde la tarea `id` y avisos. */
export const agregarBloqueo = (id: number, bloqueanteId: number) =>
  solicitar(`/tareas/${id}/bloqueos`, {
    metodo: 'POST',
    cuerpo: { bloqueanteId } satisfies AgregarBloqueoEntrada,
    esquema: resultadoTareaSchema,
  });

/** Quita el bloqueo «`id` bloqueada por `bloqueanteId`». */
export const quitarBloqueo = (id: number, bloqueanteId: number) =>
  solicitar(`/tareas/${id}/bloqueos/${bloqueanteId}`, {
    metodo: 'DELETE',
    esquema: resultadoTareaSchema,
  });

export const eliminarTarea = (id: number) => solicitar(`/tareas/${id}`, { metodo: 'DELETE' });

export const listarPersonas = () => solicitar('/usuarios', { esquema: z.array(usuarioSchema) });
